import { ClientSession } from "../utils/ClientSessionHolder";
import SessionStoreModule from "../stores/SessionStore";
import Tracking from "../tracking/Tracking";

function isHTTPS() {
	if (window.location.protocol && window.location.protocol.length > 4) {
		return window.location.protocol[4].toLowerCase() === "s";
	}

	return true;
}

export const Socket = {
	createRingBuffer: function (length: number): Record<string, any> {
		const buffer: any[] = [];
		let pointer = 0;
		return {
			push: function (item) {
				buffer[pointer] = item;
				pointer = (length + pointer + 1) % length;
				return this;
			},
			isIn: function (item) {
				return buffer.includes(item);
			},
		};
	},

	/**
	 * @connection
	 *   { channel: // Channel id as String
	 *      noAuth: // Don't check if logged in
	 *      onOpenFailed: // Function called if Websocket not supported or open of connection failed
	 *      onOpen: // Called if open
	 *      onError: // Called if an error occurred  after successful open
	 *      onClose: // called if closed
	 *      onMessage:
	 *    }
	 */
	initSocket: function (connection: Record<string, any>): WebSocket | null | undefined {
		const path = "/ordercapture/dwsocket/ws" + connection.channel;
		const prot = isHTTPS() ? "wss" : "ws";

		try {
			if (connection.noAuth === true || SessionStoreModule.SessionStore.loggedIn()) {
				const sessionId = encodeURI(ClientSession.generateSessionedCallTreeIdWithPrefix("SOCKET"));
				return new WebSocket(prot + "://" + window.location.host + path + "?callTreeId=" + sessionId);
			} else {
				Tracking.trackStructuredEvent("platform", "uidispatcher", "technical.websocketWithoutLogin", {
					tags: {
						path: path,
					},
				});
				connection.onOpenFailed("Tried to open websocket while not logged in!");
			}
		} catch (e) {
			console.error(e);
			connection.onOpenFailed(e);
		}

		return undefined;
	},
	initConnection: function (connection: Record<string, any>): Record<string, any> {
		const self = this;
		let connectionErrors = 0;
		let lastOpened = 0;

		if (!connection.channel) {
			throw new Error("No channel for websocket set");
		}

		let socket;

		function bindSocketToConnection(socketToBind) {
			socketToBind.oldHashes = self.createRingBuffer(10);

			socketToBind.onclose = function () {
				if (connection.open) {
					socket = self.initSocket(connection);

					if (socket) {
						bindSocketToConnection(socket);
					}
				} else {
					connection.onClose();
				}
			};

			socketToBind.onopen = function () {
				if (connection.version) {
					socketToBind.send(connection.version + "");
				}

				const tmpDate = lastOpened;
				lastOpened = new Date().getTime();

				if (lastOpened - tmpDate < 5000 && connectionErrors++ >= 3) {
					connection.open = false;
					connection.onClose();
				} else {
					connection.onOpen();
					// If the browser is sleeping, we might miss the onclose event and not reset the connection
					// (happens at least on a Mac with Chrome) Therefore, schedule a manual connection age check.
					setTimeout(() => {
						if (self.hasConnectionExpired(lastOpened)) {
							console.warn("WebSocket stalled: resetting connection");
							socketToBind.close();
						}
					}, 60000);
				}
			};

			socketToBind.onerror = connection.onError;

			socketToBind.onmessage = function (event) {
				if (event.data === "{'state': 'subscribed'}") {
					connection.open = true;
					return;
				}

				if (event.data === '{"state": "close"}') {
					connection.open = false;
					return;
				}

				let data;
				try {
					data = JSON.parse(event.data);
				} catch (e) {
					console.error("parsing dwsocket message error:", event.data, e);
					return;
				}

				if (data.v && data.ss_m) {
					if (!socketToBind.oldHashes.isIn(data.h)) {
						socketToBind.oldHashes.push(data.h);
						connection.onMessage(data.ss_m);
						connection.version = data.v;
					}
				} else {
					connection.onMessage(data);
				}
			};
		}

		socket = self.initSocket(connection);

		if (socket) {
			bindSocketToConnection(socket);
		} else {
			connection.open = false;
		}

		return {
			isOpen: connection.open,
			close: function () {
				connection.open = false;

				if (socket) {
					socket.close();
				}
			},
		};
	},
	hasConnectionExpired: (openSince: number): boolean => new Date().getTime() - openSince >= 50000,
};
